import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Link,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import PlaceIcon from '@mui/icons-material/Place';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BarChartIcon from '@mui/icons-material/BarChart';
import SchoolIcon from '@mui/icons-material/School';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { SAGE, CREAM, WARM } from '../theme';
import ComingSoon from '../components/shared/ComingSoon';
import SkillMatrix from '../components/jobs/SkillMatrix';
import RequirementRow from '../components/jobs/RequirementRow';
import PositionDetailModal from '../components/jobs/PositionDetailModal';
import { PositionsComparisonModal, BulkApplyModal } from '../components/jobs/PositionsActionsModals';
import ScopeBulletsModal from '../components/enhancement/ScopeBulletsModal';
import FillTheGapModal from '../components/enhancement/FillTheGapModal';
import { SelectionsProvider, useSelections } from '../hooks/useSelections';
import SelectionToggle from '../components/entity/SelectionToggle';
import { mockAuthHeaders } from '../utils/mockAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MARKET_LABELS = {
  seattle_msa: 'Seattle',
  us_remote: 'US Remote',
  global_remote: 'Global Remote',
  sf_bay_area: 'SF Bay Area',
  nyc_metro: 'NYC',
  la_metro: 'Los Angeles',
  boston_msa: 'Boston',
  chicago_msa: 'Chicago',
  dc_metro: 'Washington DC',
  austin_msa: 'Austin',
  denver_msa: 'Denver',
  atlanta_msa: 'Atlanta',
  portland_msa: 'Portland',
  us_other: 'Other US',
  non_us: 'International',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'levels', label: 'By Career Level' },
  { id: 'positions', label: 'Open Positions' },
];

const JobFamilyPage = () => {
  const { familyId } = useParams();
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get('role');
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [familyLoading, setFamilyLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Find the role info from taxonomy
  const roleInfo = taxonomy?.roles?.find((r) => r.id === roleId);
  const disciplineInfo = taxonomy?.disciplines?.find((d) => d.id === familyId);

  // Taxonomy: small + fast. Gates the page shell.
  useEffect(() => {
    setTaxonomyLoading(true);
    fetch(`${API_URL}/api/taxonomy`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setTaxonomy(data))
      .catch((err) => console.error('Failed to load taxonomy:', err))
      .finally(() => setTaxonomyLoading(false));
  }, []);

  // Family detail: slow fan-out across all companies' postings. Loaded
  // independently so the Overview content (driven by role-insights) doesn't
  // wait on it. Posting counts + Top Companies show skeletons until ready.
  useEffect(() => {
    setFamilyLoading(true);
    const roleParam = roleInfo?.name ? `?role=${encodeURIComponent(roleInfo.name)}` : '';
    fetch(`${API_URL}/api/job-families/${familyId}${roleParam}`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setFamily(data))
      .catch((err) => console.error('Failed to load family:', err))
      .finally(() => setFamilyLoading(false));
  }, [familyId, roleInfo?.name]);

  // Role-insights fetch — shared by Overview + Levels tabs.
  // Skipped at the discipline level (we curate per-role only).
  useEffect(() => {
    if (!roleId) {
      setInsights(null);
      return;
    }
    setInsightsLoading(true);
    fetch(`${API_URL}/api/role-insights/${roleId}`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setInsights(data))
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  }, [roleId]);

  // Gate only on taxonomy — that's enough to render the breadcrumb/header.
  // Family detail (slow fan-out) and role-insights load in the background.
  if (taxonomyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  // Only treat as "not found" if both lookups have failed
  if (!roleInfo && !disciplineInfo && !familyLoading && !family) {
    return (
      <Box sx={{ textAlign: 'center', py: 12 }}>
        <Typography sx={{ color: SAGE[500] }}>Job family not found: {familyId}</Typography>
      </Box>
    );
  }

  const displayName = roleInfo?.name || family?.name || disciplineInfo?.name || familyId;
  const description = roleInfo?.description || disciplineInfo?.description || '';
  const breadcrumbDiscipline = family?.discipline_name || disciplineInfo?.name || family?.name || '';
  const totalPostings = family?.total_postings;
  const levelCount = family?.levels?.length;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/explore/roles')} sx={{ color: SAGE[500] }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}
          onClick={() => navigate('/explore/roles')}
        >
          Explore Roles
        </Typography>
        <Typography sx={{ color: SAGE[300], fontSize: '0.8rem' }}>/</Typography>
        {roleId ? (
          <>
            <Typography
              sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}
              onClick={() => navigate(`/explore/roles/${familyId}`)}
            >
              {breadcrumbDiscipline}
            </Typography>
            <Typography sx={{ color: SAGE[300], fontSize: '0.8rem' }}>/</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: SAGE[700], fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
              {displayName}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '0.8rem', color: SAGE[700], fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
            {breadcrumbDiscipline}
          </Typography>
        )}
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, ml: 6 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900] }}
          >
            {displayName}
          </Typography>
          {description && (
            <Typography
              sx={{ fontSize: '0.88rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif", mt: 0.5, maxWidth: 600 }}
            >
              {description}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.82rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mt: 0.5 }}>
            {roleId ? 'Role' : 'Discipline'}
            {familyLoading ? (
              <>
                {' '}&middot; <Box component="span" sx={{ opacity: 0.5 }}>loading postings…</Box>
              </>
            ) : family ? (
              <>
                {' '}&middot; {totalPostings} active postings &middot; {levelCount || 0} career levels
              </>
            ) : null}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: 3, ml: 6 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            p: 0.5,
            borderRadius: 2.5,
            bgcolor: `${SAGE[100]}80`,
          }}
        >
          {TABS.map((tab) => (
            <Box
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 1.2,
                px: 2,
                borderRadius: 2,
                cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                fontSize: '0.85rem',
                fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? SAGE[800] : SAGE[400],
                bgcolor: activeTab === tab.id ? CREAM[50] : 'transparent',
                boxShadow: activeTab === tab.id ? `0 1px 3px ${SAGE[200]}` : 'none',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
              {tab.id === 'positions' && totalPostings != null && (
                <Typography
                  component="span"
                  sx={{ ml: 1, fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}
                >
                  {totalPostings}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ ml: 6 }}>
        {activeTab === 'overview' && (
          <OverviewTab
            family={family}
            familyId={familyId}
            roleId={roleId}
            displayName={displayName}
            insights={insights}
            insightsLoading={insightsLoading}
          />
        )}
        {activeTab === 'levels' && (
          <LevelsTab
            family={family}
            familyId={familyId}
            roleId={roleId}
            displayName={displayName}
            insights={insights}
            insightsLoading={insightsLoading}
          />
        )}
        {activeTab === 'positions' && <PositionsTab family={family} familyId={familyId} roleId={roleId} roleInfo={roleInfo} />}
      </Box>
    </Box>
  );
};


// ============================================================================
// TAB 1: OVERVIEW
// What they do (description + tiered responsibilities), comp landscape,
// top companies hiring, similar roles. Real data from /api/job-families and
// /api/role-insights; tiered responsibilities + similar_families come from
// the curated role-insights JSON when present.
// ============================================================================

const TIER_TONES = [
  { color: SAGE[700], bg: SAGE[100], border: SAGE[300] },
  { color: SAGE[600], bg: `${SAGE[100]}80`, border: SAGE[200] },
  { color: SAGE[500], bg: `${CREAM[200]}80`, border: SAGE[200] },
  { color: SAGE[400], bg: CREAM[100], border: `${SAGE[200]}80` },
];

const OverviewTab = ({ family, familyId, roleId, displayName, insights, insightsLoading }) => {
  const featurePrefix = roleId ? `role.${roleId}` : `job_family.${familyId}`;
  const navigate = useNavigate();
  const [expandedRespKey, setExpandedRespKey] = useState(null);
  const [scopeRow, setScopeRow] = useState(null);
  const [fillRow, setFillRow] = useState(null);

  const description = insights?.description || insights?.summary;
  const tiers = insights?.responsibility_tiers || [];
  // Drop the "Occasionally" / <30% tier — same editorial cut as the Levels tab
  const visibleTiers = tiers.filter((t) => {
    const pct = String(t.pct || '').toLowerCase();
    if (pct.includes('<')) return false;
    if (/occasion/i.test(t.tier || '')) return false;
    return true;
  });
  const similar = insights?.similar_families || [];
  const insightsLevels = insights?.levels || [];

  // Surface a fit-summary ribbon when items carry per-item fit data (target +
  // candidate_has). Counts items where the candidate meets/exceeds the bar.
  const fitItems = visibleTiers.flatMap((t) => (t.items || []).filter((i) => typeof i === 'object'));
  const fitTotal = fitItems.length;
  const fitMet = fitItems.filter((i) => (i.candidate_has || 0) >= (i.target || 0)).length;
  const fitPartial = fitItems.filter(
    (i) => (i.candidate_has || 0) > 0 && (i.candidate_has || 0) < (i.target || 0)
  ).length;
  const showFitRibbon = fitTotal > 0;

  // Comp range for the bar — top of P75 across all levels
  const compMaxK = Math.max(0, ...insightsLevels.map((l) => (l.comp?.base_p75 || 0) / 1000));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Role description + tiered responsibilities */}
      {insightsLoading ? (
        <SectionCard title={`What Does a ${displayName} Do?`}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} sx={{ color: SAGE[500] }} />
          </Box>
        </SectionCard>
      ) : description ? (
        <SectionCard
          title={`What Does a ${displayName} Do?`}
          subtitle={
            showFitRibbon
              ? `Where you'd already be a good fit — synthesized from ${family?.total_postings ?? 'live'} postings, scored against your profile`
              : family?.total_postings != null
                ? `Synthesized from ${family.total_postings} postings — the role varies by org, but here's how responsibilities cluster`
                : 'How responsibilities cluster across the role'
          }
        >
          <Typography
            sx={{
              fontSize: '0.92rem',
              lineHeight: 1.65,
              color: SAGE[600],
              fontFamily: "'Lora', Georgia, serif",
              mb: showFitRibbon ? 2.5 : (visibleTiers.length ? 3 : 0),
            }}
          >
            {description}
          </Typography>

          {showFitRibbon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                mb: 3,
                borderRadius: 2,
                bgcolor: `${SAGE[100]}80`,
                border: `1.5px solid ${SAGE[200]}`,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: SAGE[800],
                    fontFamily: "'Outfit', sans-serif",
                    mb: 0.25,
                  }}
                >
                  {fitMet > 0
                    ? `${fitMet} of ${fitTotal} responsibilities map directly to your background.`
                    : `${fitPartial} of ${fitTotal} responsibilities have partial fit in your background.`}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.74rem',
                    color: SAGE[500],
                    fontFamily: "'Outfit', sans-serif",
                    fontStyle: 'italic',
                  }}
                >
                  Click any row to see the evidence — and where you'd be stretching.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
                <FitChip count={fitMet} label="met" tone="met" />
                <FitChip count={fitPartial} label="partial" tone="partial" />
                <FitChip count={fitTotal - fitMet - fitPartial} label="gap" tone="gap" />
              </Box>
            </Box>
          )}

          {visibleTiers.map((t, i) => {
            const tone = TIER_TONES[i] || TIER_TONES[TIER_TONES.length - 1];
            const items = t.items || [];
            const usingCards = items.some((it) => typeof it === 'object');
            return (
              <Box key={t.tier} sx={{ mb: i === visibleTiers.length - 1 ? 0 : 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Typography
                    sx={{ fontSize: '0.85rem', fontWeight: 700, color: tone.color, fontFamily: "'Outfit', sans-serif" }}
                  >
                    {t.tier}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.65rem',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: tone.bg,
                      color: tone.color,
                      border: `1.5px solid ${tone.border}`,
                      fontWeight: 600,
                      fontFamily: "'Outfit', sans-serif",
                      letterSpacing: 0.3,
                    }}
                  >
                    {t.pct} of postings
                  </Box>
                </Box>
                {usingCards ? (
                  <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${SAGE[100]}` }}>
                    {items.map((item, j) => {
                      const obj = typeof item === 'object' ? item : { text: item };
                      const reqKey = `resp::${t.tier}::${obj.text}::${j}`;
                      return (
                        <RequirementRow
                          key={reqKey}
                          text={obj.text}
                          category={obj.category}
                          target={obj.target}
                          has={obj.candidate_has}
                          wordings={obj.wordings}
                          evidence={obj.evidence}
                          expanded={expandedRespKey === reqKey}
                          onToggle={() => setExpandedRespKey(expandedRespKey === reqKey ? null : reqKey)}
                          onIHaveThis={() => setScopeRow({
                            text: obj.text,
                            category: obj.category,
                            target: obj.target,
                            has: obj.candidate_has,
                            wordings: obj.wordings,
                            evidence: obj.evidence,
                          })}
                          onIDontHaveThis={() => setFillRow({
                            text: obj.text,
                            category: obj.category,
                            target: obj.target,
                            has: obj.candidate_has,
                            wordings: obj.wordings,
                          })}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0.5 }}>
                    {items.map((item, j) => (
                      <Box
                        key={j}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          py: 0.85,
                          px: 1.5,
                          borderRadius: 1.5,
                          bgcolor: tone.bg,
                        }}
                      >
                        <Box
                          sx={{
                            mt: 0.6,
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            bgcolor: tone.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.82rem',
                            lineHeight: 1.55,
                            color: SAGE[600],
                            fontFamily: "'Outfit', sans-serif",
                          }}
                        >
                          {typeof item === 'object' ? item.text : item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </SectionCard>
      ) : (
        <ComingSoon
          featureKey={`${featurePrefix}.description`}
          title={`What Does a ${displayName} Do?`}
          description="AI-generated role description synthesized from real job postings — responsibilities, common skills, and how the role varies across companies. Coverage is rolling out role-by-role."
          icon={<PsychologyIcon />}
        />
      )}

      {/* Career Skill Matrix — at-a-glance progression view */}
      {insights?.skills?.length > 0 && insights?.level_metadata && (
        <SectionCard
          title="Career Skill Matrix"
          subtitle="Read across a row to see how a skill deepens. Read down a column for that level's profile."
        >
          <SkillMatrix insights={insights} />
        </SectionCard>
      )}

      {/* Compensation by Level */}
      {insightsLevels.length > 0 ? (
        <SectionCard
          title="Compensation by Level"
          subtitle={insights.comp_note || 'Base salary ranges across the career ladder'}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {insightsLevels.map((lv) => {
              const p25k = (lv.comp?.base_p25 || 0) / 1000;
              const p75k = (lv.comp?.base_p75 || 0) / 1000;
              const medianK = (lv.comp?.base_median || 0) / 1000;
              const leftPct = compMaxK > 0 ? (p25k / compMaxK) * 100 : 0;
              const widthPct = compMaxK > 0 ? ((p75k - p25k) / compMaxK) * 100 : 0;
              return (
                <Box key={lv.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    sx={{
                      width: 110,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: SAGE[600],
                      fontFamily: "'Outfit', sans-serif",
                      flexShrink: 0,
                    }}
                  >
                    {lv.name}
                  </Typography>
                  <Box sx={{ flex: 1, position: 'relative', height: 22, bgcolor: SAGE[100], borderRadius: 11 }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${leftPct}%`,
                        width: `${widthPct}%`,
                        background: `linear-gradient(90deg, ${SAGE[300]}, ${SAGE[500]})`,
                        borderRadius: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.66rem',
                          fontWeight: 700,
                          color: '#fff',
                          fontFamily: "'Outfit', sans-serif",
                        }}
                      >
                        ${Math.round(medianK)}k
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      width: 110,
                      textAlign: 'right',
                      fontSize: '0.72rem',
                      color: SAGE[400],
                      fontFamily: "'Outfit', sans-serif",
                      flexShrink: 0,
                    }}
                  >
                    ${Math.round(p25k)}k – ${Math.round(p75k)}k
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      ) : !insightsLoading ? (
        <ComingSoon
          featureKey={`${featurePrefix}.compensation`}
          title="Compensation by Level"
          description="Salary bands (p25 / median / p75) for each career level. Curated per role — coverage is expanding."
          icon={<BarChartIcon />}
        />
      ) : null}

      {/* Top Companies Hiring (live data — slow fan-out, shown with skeleton) */}
      <SectionCard
        title="Top Companies Hiring"
        subtitle={`Companies with the most ${displayName} postings right now`}
      >
        {!family ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  borderRadius: 1.5,
                  bgcolor: i % 2 === 1 ? CREAM[100] : 'transparent',
                }}
              >
                <Box sx={{ width: 180, height: 14, bgcolor: SAGE[100], borderRadius: 1, opacity: 0.7 }} />
                <Box sx={{ width: 50, height: 14, bgcolor: SAGE[100], borderRadius: 1, opacity: 0.5 }} />
              </Box>
            ))}
          </Box>
        ) : family.top_companies && family.top_companies.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {family.top_companies.map((co, i) => (
              <Box
                key={co.domain}
                onClick={() => navigate(`/explore/companies/${co.domain}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  bgcolor: i % 2 === 0 ? CREAM[100] : 'transparent',
                  '&:hover': { bgcolor: SAGE[100] },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontWeight: 600, width: 20 }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      color: SAGE[800],
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {co.name || co.domain}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  {co.count} {co.count === 1 ? 'role' : 'roles'}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.85rem', color: SAGE[400] }}>No data yet</Typography>
        )}
      </SectionCard>

      {/* Similar Job Families */}
      {similar.length > 0 ? (
        <SectionCard
          title={roleId ? 'Similar Roles' : 'Similar Disciplines'}
          subtitle="Related roles you might also be a fit for — and how they differ"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {similar.map((fam) => (
              <Box
                key={fam.name}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: CREAM[100],
                  border: `2px solid ${SAGE[200]}`,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: SAGE[300], boxShadow: `0 4px 16px ${SAGE[200]}50` },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 2 }}>
                  <Typography
                    sx={{ fontSize: '0.95rem', fontWeight: 600, color: SAGE[800], fontFamily: "'Outfit', sans-serif" }}
                  >
                    {fam.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    {fam.overlap_pct != null && (
                      <Box
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          px: 1,
                          py: 0.25,
                          borderRadius: 99,
                          bgcolor: SAGE[100],
                          color: SAGE[600],
                          fontFamily: "'Outfit', sans-serif",
                        }}
                      >
                        {fam.overlap_pct}% overlap
                      </Box>
                    )}
                    {fam.comp_median_k != null && (
                      <Typography
                        sx={{ fontSize: '0.7rem', fontWeight: 600, color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}
                      >
                        ${fam.comp_median_k}k median
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{ fontSize: '0.8rem', lineHeight: 1.55, color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}
                >
                  {fam.diff}
                </Typography>
              </Box>
            ))}
          </Box>
        </SectionCard>
      ) : !insightsLoading ? (
        <ComingSoon
          featureKey={`${featurePrefix}.similar`}
          title={roleId ? 'Similar Roles' : 'Similar Disciplines'}
          description="Related roles you might also be a fit for — skill overlap percentages and how they differ. Coverage is rolling out role-by-role."
          icon={<CompareArrowsIcon />}
        />
      ) : null}

      <ScopeBulletsModal
        open={!!scopeRow}
        onClose={() => setScopeRow(null)}
        requirement={scopeRow}
        roleContext={{ role: displayName }}
      />
      <FillTheGapModal
        open={!!fillRow}
        onClose={() => setFillRow(null)}
        requirement={fillRow}
        roleContext={{ role: displayName }}
      />
    </Box>
  );
};


// ============================================================================
// TAB 2: BY CAREER LEVEL
// Wires to /api/role-insights/{roleId} when a role is selected. Falls back
// to live posting-count cards + ComingSoon stubs at the discipline level
// (we curate insights per role, not per discipline). Profile-fit overlay is
// deferred — see TODO_DEMO_PREP.md (Data piece D).
// ============================================================================
const formatCompK = (n) => `$${Math.round(n / 1000)}k`;

const LevelsTab = ({ family, familyId, roleId, displayName, insights, insightsLoading }) => {
  const navigate = useNavigate();
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [expandedReq, setExpandedReq] = useState(null);
  const [scopeRow, setScopeRow] = useState(null);
  const [fillRow, setFillRow] = useState(null);
  const featurePrefix = roleId ? `role.${roleId}` : `job_family.${familyId}`;

  // Pick a sensible default once data lands
  useEffect(() => {
    if (insights?.levels?.length && !selectedLevelId) {
      // Default to "senior" if present, else first level
      const senior = insights.levels.find((l) => l.id === 'senior');
      setSelectedLevelId(senior ? senior.id : insights.levels[0].id);
    }
  }, [insights, selectedLevelId]);

  // -- No role context: keep the discipline-level live counts + stubs --
  if (!roleId) {
    return <DisciplineLevelsFallback family={family} featurePrefix={featurePrefix} />;
  }

  if (insightsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  if (!insights) {
    return <RoleInsightsComingSoon displayName={displayName} featurePrefix={featurePrefix} />;
  }

  const levels = insights.levels || [];
  const selected = levels.find((l) => l.id === selectedLevelId) || levels[0];
  // Overlay live posting counts from the family detail when available
  const liveCountsByLevel = new Map(
    (family?.levels || []).map((l) => [String(l.level || '').toLowerCase(), l.count])
  );
  const compMaxK = Math.max(...levels.map((l) => l.comp?.base_p75 || 0)) / 1000;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Career ladder selector */}
      <SectionCard title="Career Ladder" subtitle="Select a level to see typical requirements + comp">
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {levels.map((lv) => {
            const isSelected = lv.id === selected?.id;
            const liveCount = liveCountsByLevel.get(lv.id);
            return (
              <Box
                key={lv.id}
                onClick={() => setSelectedLevelId(lv.id)}
                sx={{
                  flex: '1 1 140px',
                  maxWidth: 200,
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: isSelected ? SAGE[800] : CREAM[100],
                  border: `2px solid ${isSelected ? SAGE[800] : SAGE[200]}`,
                  color: isSelected ? CREAM[50] : SAGE[700],
                  transition: 'all 0.15s',
                  '&:hover': isSelected ? {} : { borderColor: SAGE[400] },
                }}
              >
                <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '0.95rem' }}>
                  {lv.name}
                </Typography>
                <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.7rem', opacity: 0.7, mt: 0.25 }}>
                  {lv.yoe_range}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 1.25 }}>
                  <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.7rem', opacity: 0.65 }}>
                    {liveCount != null ? `${liveCount} open` : '—'}
                  </Typography>
                  <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 700, opacity: 0.9 }}>
                    {formatCompK(lv.comp?.base_median || 0)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </SectionCard>

      {selected && (
        <>
          {/* Level summary + compensation */}
          <SectionCard title={`${selected.name} ${displayName}`} subtitle={selected.summary}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', mb: 1.5 }}>
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
                  P25
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
                  {formatCompK(selected.comp?.base_p25 || 0)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: SAGE[500], fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 }}>
                  MEDIAN BASE
                </Typography>
                <Typography sx={{ fontSize: '1.7rem', fontWeight: 700, color: SAGE[800], fontFamily: "'Outfit', sans-serif" }}>
                  {formatCompK(selected.comp?.base_median || 0)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
                  P75
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
                  {formatCompK(selected.comp?.base_p75 || 0)}
                </Typography>
              </Box>
            </Box>
            {/* Comp range bar */}
            <Box sx={{ height: 10, borderRadius: 5, bgcolor: SAGE[100], overflow: 'hidden', position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${((selected.comp?.base_p25 || 0) / 1000 / compMaxK) * 100}%`,
                  width: `${(((selected.comp?.base_p75 || 0) - (selected.comp?.base_p25 || 0)) / 1000 / compMaxK) * 100}%`,
                  top: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, ${SAGE[300]}, ${SAGE[500]})`,
                  borderRadius: 5,
                }}
              />
            </Box>
            {insights.comp_note && (
              <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mt: 1.5, fontStyle: 'italic' }}>
                {insights.comp_note}
              </Typography>
            )}
          </SectionCard>

          {/* Requirements grouped by frequency tier — target-dot fit per row */}
          <RequirementsByTier
            selected={selected}
            insights={insights}
            displayName={displayName}
            expandedReq={expandedReq}
            onToggle={(key) => setExpandedReq(expandedReq === key ? null : key)}
            onScopeRow={setScopeRow}
            onFillRow={setFillRow}
          />

          {/* Level-up CTA */}
          {(() => {
            const idx = levels.findIndex((l) => l.id === selected.id);
            const nextUp = idx >= 0 && idx < levels.length - 1 ? levels[idx + 1] : null;
            if (!nextUp) return null;
            const bump = (nextUp.comp?.base_median || 0) - (selected.comp?.base_median || 0);
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2.5,
                  borderRadius: 2.5,
                  bgcolor: `${SAGE[100]}80`,
                  border: `2px dashed ${SAGE[300]}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ArrowUpwardIcon sx={{ color: SAGE[500], fontSize: 20 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: SAGE[700], fontFamily: "'Outfit', sans-serif" }}>
                      What does it take to reach {nextUp.name}?
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
                      Median base steps up by {bump > 0 ? `+${formatCompK(bump)}` : '—'} · {nextUp.requirements?.length || 0} requirements
                    </Typography>
                  </Box>
                </Box>
                <Box
                  onClick={() => {
                    setSelectedLevelId(nextUp.id);
                    setExpandedReq(null);
                    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: SAGE[500],
                    color: CREAM[50],
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    fontFamily: "'Outfit', sans-serif",
                    flexShrink: 0,
                    '&:hover': { bgcolor: SAGE[600] },
                  }}
                >
                  See {nextUp.name} →
                </Box>
              </Box>
            );
          })()}
        </>
      )}

      <ScopeBulletsModal
        open={!!scopeRow}
        onClose={() => setScopeRow(null)}
        requirement={scopeRow}
        roleContext={{ role: displayName, level: selected?.name }}
      />
      <FillTheGapModal
        open={!!fillRow}
        onClose={() => setFillRow(null)}
        requirement={fillRow}
        roleContext={{ role: displayName, level: selected?.name }}
      />
    </Box>
  );
};

// Requirements grouped into 3 frequency tiers. Each row uses the shared
// RequirementRow with the target-dot model when curated `target` + `candidate_has`
// are present on the requirement, falling back to the frequency bar otherwise.
// Anything below 30% is dropped.
const REQ_TIERS = [
  { id: 'almost_all', label: 'Almost all',  sub: '90%+ of postings',  min: 0.90, max: 1.01 },
  { id: 'most',       label: 'Most',        sub: '60–90% of postings', min: 0.60, max: 0.90 },
  { id: 'some',       label: 'Some',        sub: '30–60% of postings', min: 0.30, max: 0.60 },
];

const RequirementsByTier = ({ selected, insights, displayName, expandedReq, onToggle, onScopeRow, onFillRow }) => {
  const all = selected.requirements || [];
  const byTier = REQ_TIERS.map((t) => ({
    ...t,
    items: all.filter((r) => (r.frequency || 0) >= t.min && (r.frequency || 0) < t.max)
              .sort((a, b) => (b.frequency || 0) - (a.frequency || 0)),
  }));
  const usingTargets = all.some((r) => r.target != null);
  const subtitle = usingTargets
    ? `Each row shows what ${selected.name} ${displayName}s typically need vs. what's in your profile. Click to expand.`
    : `Click any to see how companies word it — and start a conversation to surface your experience. ${insights.data_provenance === 'demo-curated' ? 'Preview data — refining with live aggregations.' : 'Aggregated from job postings.'}`;

  return (
    <SectionCard title={`${selected.name} Requirements`} subtitle={subtitle}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {byTier.map((tier) => {
          if (tier.items.length === 0) return null;
          return (
            <Box key={tier.id}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.25, px: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: SAGE[600],
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  {tier.label}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontStyle: 'italic' }}>
                  {tier.sub} · {tier.items.length}
                </Typography>
              </Box>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${SAGE[100]}` }}>
                {tier.items.map((req) => {
                  const reqKey = `${selected.id}::${req.text}`;
                  return (
                    <RequirementRow
                      key={reqKey}
                      text={req.text}
                      category={req.category || 'skill'}
                      target={req.target}
                      has={req.candidate_has}
                      frequency={req.frequency}
                      wordings={req.wordings}
                      evidence={req.evidence}
                      expanded={expandedReq === reqKey}
                      onToggle={() => onToggle(reqKey)}
                      onIHaveThis={() => onScopeRow({
                        text: req.text,
                        category: req.category || 'skill',
                        target: req.target,
                        has: req.candidate_has,
                        wordings: req.wordings,
                        evidence: req.evidence,
                      })}
                      onIDontHaveThis={() => onFillRow({
                        text: req.text,
                        category: req.category || 'skill',
                        target: req.target,
                        has: req.candidate_has,
                        wordings: req.wordings,
                      })}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

// Discipline-level fallback — same live posting counts as the old version,
// minus the per-level ComingSoon noise.
const DisciplineLevelsFallback = ({ family, featurePrefix }) => {
  const [selectedLevel, setSelectedLevel] = useState(null);
  useEffect(() => {
    if (family?.levels?.length && !selectedLevel) setSelectedLevel(family.levels[0].level);
  }, [family?.levels, selectedLevel]);
  if (!family) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} sx={{ color: SAGE[500] }} />
      </Box>
    );
  }
  const levels = family.levels || [];
  const selected = levels.find((l) => l.level === selectedLevel);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SectionCard title="Career Ladder" subtitle="Select a level to see availability">
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {levels.map((lv) => {
            const isSelected = lv.level === selectedLevel;
            return (
              <Box
                key={lv.level}
                onClick={() => setSelectedLevel(lv.level)}
                sx={{
                  flex: '1 1 120px', maxWidth: 180, p: 2, borderRadius: 2, cursor: 'pointer',
                  bgcolor: isSelected ? SAGE[800] : CREAM[100],
                  border: `2px solid ${isSelected ? SAGE[800] : SAGE[200]}`,
                  color: isSelected ? CREAM[50] : SAGE[700], transition: 'all 0.15s',
                  '&:hover': isSelected ? {} : { borderColor: SAGE[400] },
                }}
              >
                <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.9rem' }}>
                  {lv.level}
                </Typography>
                <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.75rem', opacity: 0.7, mt: 0.5 }}>
                  {lv.count} roles
                </Typography>
              </Box>
            );
          })}
        </Box>
      </SectionCard>
      {selected && (
        <SectionCard title={`${selected.level} — Market Availability`}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <SmallStat label="Seattle" value={selected.seattle_count} icon={<PlaceIcon />} color={SAGE[600]} />
            <SmallStat label="Remote" value={selected.remote_count} icon={<HomeWorkIcon />} color="#6366f1" />
            <SmallStat label="Total" value={selected.count} icon={<WorkIcon />} color={SAGE[400]} />
          </Box>
        </SectionCard>
      )}
      <ComingSoon
        featureKey={`${featurePrefix}.requirements`}
        title="Per-Level Requirements"
        description="Drill into a specific role within this discipline to see typical requirements, comp, and what hiring managers look for at each level."
        icon={<SchoolIcon />}
      />
    </Box>
  );
};

const RoleInsightsComingSoon = ({ displayName, featurePrefix }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
    <ComingSoon
      featureKey={`${featurePrefix}.requirements`}
      title={`${displayName} — Career Levels`}
      description="We're still aggregating requirements + comp data for this role. Data Scientist is the seed — more roles light up as we expand coverage."
      icon={<SchoolIcon />}
    />
  </Box>
);


// ============================================================================
// TAB 3: OPEN POSITIONS (table layout)
// ============================================================================
// PositionsTab is split: outer wraps in SelectionsProvider so the modal +
// action bar can read/write selection state; inner does the table/UI.
const PositionsTab = (props) => (
  <SelectionsProvider>
    <PositionsTabInner {...props} />
  </SelectionsProvider>
);

const PositionsTabInner = ({ family, familyId, roleId, roleInfo }) => {
  const navigate = useNavigate();
  // The curated PMM demo short-circuit is disabled: its fixtures
  // (PMM_DEMO_POSITIONS / PMM_DEMO_FACETS) were never committed, so the branch
  // referenced undefined identifiers and failed the production build. Demo
  // users fall through to the real backend — slower, but correct. Re-enable by
  // adding the fixtures (alongside the others in utils/demoMode.js) and
  // restoring the useIsDemoUser check here.
  const usePmmDemo = false;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ levels: [], companies: [] });
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState(null);
  const [marketFilter, setMarketFilter] = useState(null);
  const [activePosition, setActivePosition] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { listType, getState, cycle } = useSelections('positions');
  const selectionEntries = listType();
  const savedIdSet = useMemo(
    () => new Set(selectionEntries.filter(([_, s]) => s === 'saved').map(([id]) => id)),
    [selectionEntries]
  );
  const consideringIdSet = useMemo(
    () => new Set(selectionEntries.filter(([_, s]) => s === 'considering').map(([id]) => id)),
    [selectionEntries]
  );

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleInfo?.name) params.set('role', roleInfo.name);
      if (search) params.set('q', search);
      if (levelFilter) params.set('level', levelFilter);
      if (marketFilter) params.set('market', marketFilter);
      params.set('limit', '200');

      const res = await fetch(`${API_URL}/api/job-families/${familyId}/jobs?${params}`, {
        headers: { ...mockAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json();
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
      setFacets(data.facets || { levels: [], companies: [] });
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [usePmmDemo, familyId, roleInfo?.name, search, levelFilter, marketFilter]);

  useEffect(() => {
    const timeout = setTimeout(fetchJobs, usePmmDemo ? 0 : 200);
    return () => clearTimeout(timeout);
  }, [fetchJobs, usePmmDemo]);

  // Local search/filter for the demo path (since we skip the API).
  const visibleJobs = useMemo(() => {
    if (!usePmmDemo) return jobs;
    return jobs.filter((j) => {
      if (search) {
        const hay = `${j.title} ${j.company_name}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      if (levelFilter && j.level !== levelFilter) return false;
      if (marketFilter && !(j.markets || []).includes(marketFilter)) return false;
      return true;
    });
  }, [usePmmDemo, jobs, search, levelFilter, marketFilter]);

  const savedPositions = useMemo(
    () => jobs.filter((j) => savedIdSet.has(j.job_id)),
    [jobs, savedIdSet]
  );
  const savedCount = savedPositions.length;
  const consideringCount = consideringIdSet.size;
  const totalSelected = savedCount + consideringCount;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search positions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 260,
            '& .MuiOutlinedInput-root': {
              bgcolor: CREAM[50],
              fontFamily: "'Outfit', sans-serif",
              borderRadius: 2,
              '& fieldset': { borderColor: SAGE[200] },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: SAGE[400], fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(facets.levels || []).map((lv) => (
            <Chip
              key={lv}
              label={lv}
              size="small"
              onClick={() => setLevelFilter(levelFilter === lv ? null : lv)}
              sx={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '0.72rem',
                bgcolor: levelFilter === lv ? SAGE[600] : CREAM[200],
                color: levelFilter === lv ? '#fff' : SAGE[600],
                '&:hover': { opacity: 0.85 },
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip
            label="Seattle"
            size="small"
            onClick={() => setMarketFilter(marketFilter === 'seattle_msa' ? null : 'seattle_msa')}
            sx={{
              fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem',
              bgcolor: marketFilter === 'seattle_msa' ? SAGE[600] : CREAM[200],
              color: marketFilter === 'seattle_msa' ? '#fff' : SAGE[600],
            }}
          />
          <Chip
            label="Remote"
            size="small"
            onClick={() => setMarketFilter(marketFilter === 'us_remote' ? null : 'us_remote')}
            sx={{
              fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem',
              bgcolor: marketFilter === 'us_remote' ? '#6366f1' : CREAM[200],
              color: marketFilter === 'us_remote' ? '#fff' : SAGE[600],
            }}
          />
        </Box>
      </Box>

      <Typography sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 2 }}>
        {jobs.length} positions{levelFilter ? ` at ${levelFilter} level` : ''}{marketFilter ? ` in ${marketFilter === 'seattle_msa' ? 'Seattle' : 'Remote'}` : ''}
      </Typography>

      {/* Selection action bar \u2014 shows when the user has saved/considering items */}
      {totalSelected > 0 && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            px: 2, py: 1.25, mb: 2, borderRadius: 2,
            bgcolor: SAGE[700], color: CREAM[50],
            boxShadow: `0 4px 14px -4px ${SAGE[700]}55`,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', fontWeight: 600 }}>
              {savedCount > 0 ? `${savedCount} saved` : ''}{savedCount > 0 && consideringCount > 0 ? ' \u00b7 ' : ''}{consideringCount > 0 ? `${consideringCount} considering` : ''}
            </Typography>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.74rem', opacity: 0.75, fontStyle: 'italic' }}>
              Compare side-by-side, or move them into the bulk resume builder.
            </Typography>
          </Box>
          <Box
            onClick={() => setCompareOpen(true)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.85, borderRadius: 1.5,
              border: `1.5px solid ${CREAM[50]}55`, color: CREAM[50],
              cursor: savedCount === 0 ? 'not-allowed' : 'pointer',
              opacity: savedCount === 0 ? 0.5 : 1,
              fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 600,
              '&:hover': savedCount > 0 ? { bgcolor: `${CREAM[50]}15` } : {},
            }}
          >
            Compare ({savedCount})
          </Box>
          <Box
            onClick={() => setBulkOpen(true)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.85, borderRadius: 1.5,
              bgcolor: WARM.accent, color: '#fff',
              cursor: savedCount === 0 ? 'not-allowed' : 'pointer',
              opacity: savedCount === 0 ? 0.5 : 1,
              fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 700,
              '&:hover': savedCount > 0 ? { bgcolor: '#a87b56' } : {},
            }}
          >
            Build resumes for {savedCount} \u2192
          </Box>
        </Box>
      )}

      {/* Table */}
      <Box sx={{ bgcolor: CREAM[50], borderRadius: 2.5, border: `1px solid ${SAGE[200]}`, overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 2fr 1fr 1fr 0.8fr 0.6fr 48px',
            gap: 1.5,
            px: 2.5,
            py: 1.2,
            bgcolor: CREAM[100],
            borderBottom: `1px solid ${SAGE[100]}`,
          }}
        >
          {['', 'Title', 'Company', 'Market', 'Level', 'Posted', ''].map((col, i) => (
            <Typography
              key={i}
              sx={{
                fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.68rem',
                color: SAGE[500], textTransform: 'uppercase', letterSpacing: '0.05em',
              }}
            >
              {col}
            </Typography>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: SAGE[500] }} />
          </Box>
        ) : visibleJobs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <WorkIcon sx={{ fontSize: 36, color: SAGE[300], mb: 1 }} />
            <Typography sx={{ color: SAGE[500], fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem' }}>
              No positions match your filters
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'auto' }}>
            {jobs.map((job, idx) => {
              const sel = getState(job.job_id);
              const isSaved = sel === 'saved';
              const isConsidering = sel === 'considering';
              return (
                <Box
                  key={job.job_id}
                  onClick={() => setActivePosition(job)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr 0.8fr 0.6fr 48px',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.3,
                    alignItems: 'center',
                    borderBottom: idx < jobs.length - 1 ? `1px solid ${SAGE[50]}` : 'none',
                    transition: 'background 0.1s',
                    cursor: 'pointer',
                    bgcolor: isSaved ? `${WARM.accent}10` : isConsidering ? `${SAGE[100]}55` : 'transparent',
                    '&:hover': { bgcolor: isSaved ? `${WARM.accent}18` : `${SAGE[50]}80` },
                  }}
                >
                  {/* Selection toggle */}
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex' }}>
                    <SelectionToggle
                      state={sel}
                      onCycle={() => cycle(job.job_id)}
                      size="small"
                    />
                  </Box>

                  {/* Title */}
                  <Tooltip title={job.title} placement="top-start" arrow>
                    <Typography
                      sx={{
                        fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.83rem',
                        color: SAGE[800], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      {job.title}
                    </Typography>
                  </Tooltip>

                  {/* Company */}
                  <Typography
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/explore/companies/${job.company_domain}`);
                    }}
                    sx={{
                      fontFamily: "'Outfit', sans-serif", fontSize: '0.8rem', color: SAGE[600],
                      cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      '&:hover': { color: SAGE[800], textDecoration: 'underline' },
                    }}
                  >
                    {job.company_name}
                  </Typography>

                  {/* Market */}
                  <Tooltip title={job.location || ''} placement="top" arrow>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
                      {job.markets?.length > 0 ? (
                        job.markets.map((m) => (
                          <Chip
                            key={m}
                            label={MARKET_LABELS[m] || m}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.63rem', fontFamily: "'Outfit', sans-serif",
                              bgcolor: m === 'us_remote' ? '#6366f115' : `${SAGE[600]}10`,
                              color: m === 'us_remote' ? '#6366f1' : SAGE[600], fontWeight: 500,
                            }}
                          />
                        ))
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: SAGE[400] }}>
                          {job.location || '\u2014'}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>

                  {/* Level */}
                  <Typography sx={{ fontSize: '0.8rem', color: SAGE[600], fontFamily: "'Outfit', sans-serif" }}>
                    {job.level || '\u2014'}
                  </Typography>

                  {/* Posted */}
                  <Typography sx={{ fontSize: '0.73rem', color: SAGE[400] }}>
                    {formatDate(job.updated_at)}
                  </Typography>

                  {/* External link */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {job.url && (
                      <Tooltip title="View source posting" arrow>
                        <Link
                          href={job.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: SAGE[400], display: 'flex', '&:hover': { color: SAGE[600] } }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </Link>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {!loading && visibleJobs.length > 0 && (
          <Box sx={{ px: 2.5, py: 1.2, borderTop: `1px solid ${SAGE[100]}`, bgcolor: CREAM[100] }}>
            <Typography variant="caption" sx={{ color: SAGE[400] }}>
              Showing {visibleJobs.length} of {total} positions
            </Typography>
          </Box>
        )}
      </Box>

      <PositionDetailModal
        open={!!activePosition}
        onClose={() => setActivePosition(null)}
        position={activePosition}
      />
      <PositionsComparisonModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        positions={savedPositions}
      />
      <BulkApplyModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        positions={savedPositions}
      />
    </Box>
  );
};


// ============================================================================
// SHARED COMPONENTS
// ============================================================================

const SectionCard = ({ title, subtitle, children }) => (
  <Box
    sx={{
      p: 3,
      bgcolor: CREAM[50],
      borderRadius: 2.5,
      border: `2px solid ${SAGE[200]}`,
    }}
  >
    <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1rem', color: SAGE[800], mb: 0.3 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const SmallStat = ({ label, value, icon, color }) => (
  <Box
    sx={{
      flex: '1 1 100px',
      p: 1.5,
      bgcolor: CREAM[100],
      borderRadius: 2,
      border: `1px solid ${SAGE[100]}`,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Box sx={{ color, opacity: 0.6 }}>{React.cloneElement(icon, { sx: { fontSize: 22 } })}</Box>
    <Box>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color, fontFamily: "'Outfit', sans-serif", lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const FIT_CHIP_TONES = {
  met:     { bg: '#3d7a4a18', color: '#3d7a4a' },
  partial: { bg: '#c4956a20', color: '#c4956a' },
  gap:     { bg: '#c48a8a20', color: '#c48a8a' },
};

const FitChip = ({ count, label, tone }) => {
  if (!count) return null;
  const t = FIT_CHIP_TONES[tone] || FIT_CHIP_TONES.met;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: t.bg,
      }}
    >
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: t.color, fontFamily: "'Outfit', sans-serif", lineHeight: 1 }}>
        {count}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: t.color, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
    </Box>
  );
};


export default JobFamilyPage;
